import { Router, ActivatedRoute } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { asyncScheduler, Observable, of } from 'rxjs';
import { catchError, map, observeOn, shareReplay, tap } from 'rxjs/operators';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

import { environment as env } from '@env/environment';
import { HttpErrorHandler } from './http-error-handler';
import { AuthenticationService } from './AuthenticationService';
import { PagedResult } from '../interfaces/IPagedResult';
import { EconomyTodoItem } from '../interfaces/EconomyTodoItem';


// 🔹 Interface för EmployerCertificateRequest
export interface EmployerCertificateRequestResponse {
  id: number;
  invoiceId: number;
  invoiceNumber?: string;
  employerCertificateUrl?: string;
  createdAt: Date;
  completedAt?: Date;
  statusName: string;
  userFullName: string;
  userEmail: string;
  userPersonalNumber: string;
}

export interface PreNotifiedInvoiceRowResponse {
  InvoiceRowId: number;
  InvoiceId?: number;
  UserId?: string;
  UserFullName: string;
  UserPersonalNumber?: string;
  StartDate?: Date | string;
  HoursWorked?: number;
  MinutesWorked?: number;
}

export interface PreNotifiedCalendarCount {
  Date: Date | string;
  Count: number;
}

export interface EmployerCertificatePendingCountResponse {
  Count?: number;
  count?: number;
}


@Injectable()
export class AdminTaskService {
  public apiAuthUrl: string;
  private apiBaseUrl: string;
  public token: string;
  private userString: string;
  private httpOptions: any;
  private preNotifiedCurrentCountDate = '';
  private preNotifiedCurrentCountCache: number | null = null;
  private preNotifiedCurrentCountRequest$: Observable<number> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private httpErrorHandler: HttpErrorHandler,
    private route: ActivatedRoute,
    public snackBar: MatSnackBar,
    public authService: AuthenticationService
  ) {
    this.apiBaseUrl = env.hostApi;
    this.setHttpOptions();
  }

  showMessage(message: string) {
    let config = new MatSnackBarConfig();
    config.duration = 7000;
    this.snackBar.open(message, undefined, config);
  }

  // 🔹 Hämtar utbetalningar
  getTasks(
    page: number = 1,
    pageSize: number = 10,
    search: string = '',
    sortBy: string = 'id',
    sortDirection: string = 'asc',
    paymentDateFrom?: string | null,
    paymentDateTo?: string | null
  ): Observable<PagedResult<EconomyTodoItem>> {
    const url = `${this.apiBaseUrl}admin/tasks`;

    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (search.trim()) {
      params = params.set('search', search.trim());
    }

    if (paymentDateFrom) {
      params = params.set('paymentDateFrom', paymentDateFrom);
    }

    if (paymentDateTo) {
      params = params.set('paymentDateTo', paymentDateTo);
    }

    return this.http.get<PagedResult<EconomyTodoItem>>(url, {
      headers: this.httpOptions.headers,
      params
    }).pipe(
      tap(response => console.log('Fetched paged tasks:', response))
    );
  }

  generateEmployerCertificate(requestId: number): Observable<any> {
    const url = `${this.apiBaseUrl}admin/employercertificates/${requestId}/generate`;
    return this.http.post(url, {}, {
      headers: this.httpOptions.headers || new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  uploadEmployerCertificateDocument(requestId: number, fileGuid: string): Observable<any> {
    const url = `${this.apiBaseUrl}admin/employercertificates/${requestId}/document`;
    return this.http.post<any>(url, { fileGuid }, {
      headers: this.httpOptions.headers || new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  
  deleteEmployerCertificateRequest(requestId: number): Observable<any> {
    const url = `${this.apiBaseUrl}admin/employercertificates/${requestId}`;
    return this.http.delete(url, {
      headers: this.httpOptions.headers || new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }


  // 🔹 Hämtar arbetsgivarintyg
  getEmployerCertificates(
    page: number = 1,
    pageSize: number = 10,
    search: string = '',
    sortBy: string = 'id',
    sortDirection: string = 'asc',
    statuses: string[] = []
  ): Observable<PagedResult<EmployerCertificateRequestResponse>> {
    const url = `${this.apiBaseUrl}admin/employercertificates`;

    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (search.trim()) {
      params = params.set('search', search.trim());
    }

    if (Array.isArray(statuses) && statuses.length > 0) {
      params = params.set('statuses', statuses.join(','));
    }

    return this.http.get<PagedResult<EmployerCertificateRequestResponse>>(url, {
      headers: this.httpOptions.headers,
      params
    }).pipe(
      tap(response => console.log('Fetched employer certificates:', response))
    );
  }

  getEmployerCertificatePendingCount(): Observable<number> {
    const url = `${this.apiBaseUrl}admin/employercertificates/pending-count`;

    return this.http.get<EmployerCertificatePendingCountResponse>(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map(response => Number(response?.Count ?? response?.count ?? 0)),
      catchError(() => of(-1))
    );
  }

  getPreNotifiedInvoiceRows(
    page: number = 1,
    pageSize: number = 25,
    search: string = '',
    sortBy: string = 'startDate',
    sortDirection: string = 'asc',
    startDateFrom?: string | null,
    startDateTo?: string | null
  ): Observable<PagedResult<PreNotifiedInvoiceRowResponse>> {
    const url = `${this.apiBaseUrl}admin/prenotified-invoice-rows`;

    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);

    if (search.trim()) {
      params = params.set('search', search.trim());
    }

    if (startDateFrom) {
      params = params.set('startDateFrom', startDateFrom);
    }

    if (startDateTo) {
      params = params.set('startDateTo', startDateTo);
    }

    return this.http.get<PagedResult<PreNotifiedInvoiceRowResponse>>(url, {
      headers: this.httpOptions.headers,
      params
    }).pipe(
      tap(response => console.log('Fetched pre-notified invoice rows:', response))
    );
  }

  getPreNotifiedCurrentCount(date: string): Observable<number> {
    if (this.preNotifiedCurrentCountDate === date && this.preNotifiedCurrentCountCache !== null) {
      return of(this.preNotifiedCurrentCountCache).pipe(observeOn(asyncScheduler));
    }

    if (this.preNotifiedCurrentCountDate === date && this.preNotifiedCurrentCountRequest$) {
      return this.preNotifiedCurrentCountRequest$.pipe(observeOn(asyncScheduler));
    }

    this.preNotifiedCurrentCountDate = date;
    this.preNotifiedCurrentCountCache = null;
    this.preNotifiedCurrentCountRequest$ = this.getPreNotifiedInvoiceRows(
      1,
      1,
      '',
      'startDate',
      'asc',
      date,
      date
    ).pipe(
      map(result => Number(result?.TotalCount ?? 0)),
      tap(count => this.preNotifiedCurrentCountCache = count),
      catchError(() => of(0)),
      shareReplay(1)
    );

    return this.preNotifiedCurrentCountRequest$.pipe(observeOn(asyncScheduler));
  }

  getPreNotifiedCalendarCounts(
    startDateFrom?: string | null,
    startDateTo?: string | null
  ): Observable<PreNotifiedCalendarCount[]> {
    const url = `${this.apiBaseUrl}admin/prenotified-invoice-rows/calendar`;

    let params = new HttpParams();

    if (startDateFrom) {
      params = params.set('startDateFrom', startDateFrom);
    }

    if (startDateTo) {
      params = params.set('startDateTo', startDateTo);
    }

    return this.http.get<PreNotifiedCalendarCount[]>(url, {
      headers: this.httpOptions.headers,
      params
    }).pipe(
      tap(response => console.log('Fetched pre-notified calendar counts:', response))
    );
  }

  setHttpOptions() {
    if (this.authService.userData.Jwt == null) return;

    var bearerToken = 'bearer ' + this.authService.userData.Jwt;

    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': bearerToken
      })
    };
  }
}

